/**
 * ConfigurationSummary - Configuration 요약 표시 컴포넌트
 * 학습 순서와 주요 옵션을 간단히 요약하여 표시
 */
import { Settings, Play, ClipboardList, FileText, Edit2, Plus, Loader2 } from 'lucide-react'
import type { LessonConfiguration, ComponentConfig, QuizLocation } from '@/types/configuration'
import { QUIZ_LOCATION_LABELS } from '@/types/configuration'

interface ConfigurationSummaryProps {
  configuration: LessonConfiguration | null
  onEdit: () => void
  isLoading?: boolean
  titleClassName?: string
}

// 컴포넌트 표시 이름 가져오기
const getComponentDisplayName = (component: ComponentConfig): string => {
  if (component.type === 'quiz' && component.location) {
    // QUIZ_LOCATION_LABELS에서 간결한 이름 추출 (예: "Pre-Quiz (사전 평가)" → "Pre Quiz")
    const fullLabel = QUIZ_LOCATION_LABELS[component.location as QuizLocation]
    // 괄호 앞 부분만 사용하고 하이픈 제거
    return fullLabel?.split(' (')[0].replace('-', ' ') || 'Quiz'
  }
  const names: Record<string, string> = {
    video: 'Video',
    assignment: '생각 정리하기',
    reading: '읽기',
    interactive: '인터랙티브',
    discussion: '토론',
    download: '다운로드'
  }
  return names[component.type] || component.type
}

// 컴포넌트 아이콘 가져오기
const getComponentIcon = (type: string) => {
  switch (type) {
    case 'video':
      return <Play className="w-3 h-3" />
    case 'quiz':
      return <ClipboardList className="w-3 h-3" />
    case 'assignment':
      return <FileText className="w-3 h-3" />
    default:
      return null
  }
}

// 비디오 옵션 요약
const getVideoOptionsSummary = (config: LessonConfiguration): string[] => {
  const videoComponent = config.sequence.find(c => c.type === 'video' && c.enabled)
  if (!videoComponent?.options) return []

  const summaries: string[] = []
  const opts = videoComponent.options

  if (opts.allowSkip) {
    summaries.push('스킵 허용')
  } else if (opts.allowSkipAfterFirstWatch) {
    summaries.push('첫 시청 후 스킵')
  } else {
    summaries.push('스킵 불가')
  }

  if (opts.requiredWatchPercent) {
    summaries.push(`${opts.requiredWatchPercent}% 필수`)
  }

  if (opts.allowPlaybackSpeed) {
    summaries.push('배속 가능')
  }

  return summaries
}

// 퀴즈 옵션 요약
const getQuizOptionsSummary = (config: LessonConfiguration): string[] => {
  const quizComponents = config.sequence.filter(c => c.type === 'quiz' && c.enabled)
  if (quizComponents.length === 0) return []

  // Post Quiz 옵션 우선 사용
  const mainQuiz = quizComponents.find(q => q.location === 'post') || quizComponents[0]
  if (!mainQuiz?.options) return []

  const summaries: string[] = []
  const opts = mainQuiz.options

  if (opts.allowRetry) {
    if (opts.maxAttempts === 0) {
      summaries.push('무제한 재시도')
    } else if (opts.maxAttempts) {
      summaries.push(`${opts.maxAttempts}회 재시도`)
    }
  } else {
    summaries.push('재시도 불가')
  }

  if (opts.passingScore) {
    summaries.push(`${opts.passingScore}% 통과`)
  }

  if (opts.showAnswerImmediately) {
    summaries.push('즉시 정답')
  }

  return summaries
}

export function ConfigurationSummary({ configuration, onEdit, isLoading, titleClassName }: ConfigurationSummaryProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-3 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">설정 로딩 중...</span>
      </div>
    )
  }

  // Configuration이 없는 경우
  if (!configuration) {
    return (
      <div>
        <div className={`flex items-center justify-between mb-3 ${titleClassName || ''}`}>
          <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Settings className="w-4 h-4 text-indigo-600" />
            학습진행 설정
          </h4>
          <button
            onClick={onEdit}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-md transition-colors"
          >
            <Plus className="w-3 h-3" />
            설정 추가
          </button>
        </div>
        <div className="p-3 bg-slate-50 rounded-lg">
          <p className="text-sm text-slate-500">설정 없음 (기본값 사용)</p>
        </div>
      </div>
    )
  }

  // 활성화된 컴포넌트만 필터링
  const enabledComponents = configuration.sequence.filter(c => c.enabled)
  const videoSummary = getVideoOptionsSummary(configuration)
  const quizSummary = getQuizOptionsSummary(configuration)

  return (
    <div>
      <div className={`flex items-center gap-3 mb-3 ${titleClassName || ''}`}>
        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Settings className="w-4 h-4 text-indigo-600" />
          학습진행 설정
        </h4>
        <button
          onClick={onEdit}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-md transition-colors"
        >
          <Edit2 className="w-3 h-3" />
          편집
        </button>
      </div>

      <div className="p-3 bg-slate-50 rounded-lg space-y-3">
        {/* 학습 순서 */}
        <div>
          <span className="text-xs font-medium text-slate-500">학습 순서</span>
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {enabledComponents.map((component, index) => (
              <div key={`${component.type}-${component.location || index}`} className="flex items-center">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                  component.required
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-slate-200 text-slate-600'
                }`}>
                  {getComponentIcon(component.type)}
                  {getComponentDisplayName(component)}
                </span>
                {index < enabledComponents.length - 1 && (
                  <span className="text-slate-400 mx-1">→</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 비디오 옵션 */}
        {videoSummary.length > 0 && (
          <div>
            <span className="text-xs font-medium text-slate-500">비디오</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {videoSummary.map((item, i) => (
                <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* 퀴즈 옵션 */}
        {quizSummary.length > 0 && (
          <div>
            <span className="text-xs font-medium text-slate-500">퀴즈</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {quizSummary.map((item, i) => (
                <span key={i} className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* 레슨 간 순서 및 컴포넌트 순차 진행 */}
        <div className="pt-2 border-t border-slate-200 space-y-1">
          {/* 레슨 간 순서 */}
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
              configuration.completionRules.lessonSequenceMode === 'flexible'
                ? 'bg-green-50 text-green-700'
                : 'bg-purple-50 text-purple-700'
            }`}>
              레슨: {configuration.completionRules.lessonSequenceMode === 'flexible' ? '자유' : '순차'}
            </span>
            {configuration.completionRules.lessonSequenceOverridable !== false && (
              <span className="text-xs text-slate-400">(하위에서 변경 가능)</span>
            )}
          </div>
          {/* 컴포넌트 순차 진행 */}
          {configuration.completionRules.enforceSequentialProgress && (
            <span className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
              컴포넌트 순차 진행
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
